import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Menu,
  MenuItem,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  AccountCircle as AccountIcon,
  Person as PersonIcon,
  ExitToApp as LogoutIcon,
  CreditCard as CreditCardIcon,
  Add as AddIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import { useCredits } from '../contexts/CreditContext';
import { useNotifications } from '../contexts/NotificationContext';
import { supabase } from '../lib/supabaseClient';

interface HeaderProps {
  onLogin?: () => void;
  onRegister?: () => void;
  onPostProject?: () => void;
  onFindTalent?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  onLogin,
  onRegister,
  onPostProject,
  onFindTalent,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { credits, refreshCredits } = useCredits();
  const { unreadCount } = useNotifications();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
  const [authMenuAnchor, setAuthMenuAnchor] = useState<null | HTMLElement>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // Initialize with cached value if available
    if (typeof window !== 'undefined') {
      return localStorage.getItem('isLoggedIn') === 'true';
    }
    return false;
  });
  const [userRole, setUserRole] = useState<string | null>(() => {
    // Initialize with cached value if available
    if (typeof window !== 'undefined') {
      return localStorage.getItem('userRole') || null;
    }
    return null;
  });
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Debug: Log state changes
  useEffect(() => {
    console.log('Header state changed - isLoggedIn:', isLoggedIn, 'userRole:', userRole, 'isInitialized:', isInitialized);
  }, [isLoggedIn, userRole, isInitialized]);

  // Check login state from Supabase
  useEffect(() => {
    const checkLoginState = async () => {
      try {
        // First, try to get cached data for immediate UI update
        const cachedLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
        const cachedUserRole = localStorage.getItem('userRole');
        
        if (cachedLoggedIn && cachedUserRole) {
          console.log('Using cached data - isLoggedIn:', cachedLoggedIn, 'userRole:', cachedUserRole);
          setIsLoggedIn(cachedLoggedIn);
          setUserRole(cachedUserRole);
          setIsInitialized(true);
        }
        
        // Then check Supabase authentication
        const { data: { user } } = await supabase.auth.getUser();
        const loggedIn = !!user;
        console.log('checkLoginState - loggedIn:', loggedIn, 'user:', user);
        
        // Only update state if it's different to prevent unnecessary re-renders
        setIsLoggedIn(prevLoggedIn => {
          if (prevLoggedIn !== loggedIn) {
            console.log('Updating isLoggedIn from', prevLoggedIn, 'to', loggedIn);
            localStorage.setItem('isLoggedIn', loggedIn.toString());
            return loggedIn;
          }
          return prevLoggedIn;
        });
        
        if (loggedIn && user) {
          try {
            // Get user role from profile
            const { getCurrentUserProfile } = await import('../lib/api/profile');
            const profile = await getCurrentUserProfile();
            console.log('checkLoginState - profile:', profile);
            if (profile) {
              console.log('Setting userRole to:', profile.role);
              setUserRole(prevRole => {
                if (prevRole !== profile.role) {
                  console.log('Updating userRole from', prevRole, 'to', profile.role);
                  localStorage.setItem('userRole', profile.role);
                  return profile.role;
                }
                return prevRole;
              });
            } else {
              console.log('No profile found, setting userRole to null');
              console.log('User ID:', user.id, 'Email:', user.email);
              setUserRole(prevRole => {
                if (prevRole !== null) {
                  console.log('Updating userRole from', prevRole, 'to null');
                  localStorage.removeItem('userRole');
                  return null;
                }
                return prevRole;
              });
            }
          } catch (error) {
            console.error('Error fetching user profile:', error);
            console.log('User ID:', user.id, 'Email:', user.email);
            setUserRole(prevRole => {
              if (prevRole !== null) {
                console.log('Updating userRole from', prevRole, 'to null due to error');
                localStorage.removeItem('userRole');
                return null;
              }
              return prevRole;
            });
          }
        } else {
          console.log('Not logged in, setting userRole to null');
          console.log('User object:', user);
          setUserRole(prevRole => {
            if (prevRole !== null) {
              console.log('Updating userRole from', prevRole, 'to null - not logged in');
              localStorage.removeItem('userRole');
              return null;
            }
            return prevRole;
          });
          localStorage.setItem('isLoggedIn', 'false');
        }
      } catch (error) {
        console.error('Error checking login state:', error);
        setIsLoggedIn(false);
        setUserRole(null);
        localStorage.setItem('isLoggedIn', 'false');
        localStorage.removeItem('userRole');
      } finally {
        setIsInitialized(true);
      }
    };

    // Check on mount
    checkLoginState();

    // Listen for login/logout events
    window.addEventListener('loginSuccess', checkLoginState);
    window.addEventListener('logout', checkLoginState);

    return () => {
      window.removeEventListener('loginSuccess', checkLoginState);
      window.removeEventListener('logout', checkLoginState);
    };
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleAccountMenuOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setAccountMenuAnchor(event.currentTarget);
    
    // Refresh credits when opening account menu
    if (isLoggedIn) {
      try {
        await refreshCredits();
        console.log('Credits refreshed when opening account menu');
      } catch (error) {
        console.error('Error refreshing credits:', error);
      }
    }
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
  };

  const handleLogout = async () => {
    try {
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // Clear local state
      setIsLoggedIn(false);
      setUserRole(null);
      setAccountMenuAnchor(null);
      
      // Clear localStorage cache
      localStorage.setItem('isLoggedIn', 'false');
      localStorage.removeItem('userRole');
      
      // Dispatch logout event
      window.dispatchEvent(new CustomEvent('logout'));
      
      navigate('/');
    } catch (error) {
      console.error('Error during logout:', error);
      // Still clear local state even if logout fails
      setIsLoggedIn(false);
      setUserRole(null);
      setAccountMenuAnchor(null);
      
      // Clear localStorage cache
      localStorage.setItem('isLoggedIn', 'false');
      localStorage.removeItem('userRole');
      
      navigate('/');
    }
  };

  const handleViewProfile = () => {
    navigate('/profile');
    setAccountMenuAnchor(null);
  };

  const handleCreditHistory = () => {
    navigate('/credits/history');
    setAccountMenuAnchor(null);
  };

  const handleBuyCredits = () => {
    navigate('/credits');
    setAccountMenuAnchor(null);
  };

  const handleAuthMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAuthMenuAnchor(event.currentTarget);
  };

  const handleAuthMenuClose = () => {
    setAuthMenuAnchor(null);
  };

  const handleLogin = () => {
    setMobileOpen(false);
    handleAuthMenuClose();
    if (onLogin) {
      onLogin();
    } else {
      navigate('/login');
    }
  };

  const handleRegister = () => {
    setMobileOpen(false);
    handleAuthMenuClose();
    if (onRegister) {
      onRegister();
    } else {
      navigate('/register');
    }
  };

  const handlePostProject = () => {
    setMobileOpen(false);
    console.log('handlePostProject called, userRole:', userRole, 'isLoggedIn:', isLoggedIn);
    
    if (onPostProject) {
      onPostProject();
    } else {
      // Navigate based on user role
      if (userRole === 'CLIENT') {
        console.log('Navigating to /new-project');
        navigate('/new-project');
      } else if (userRole === 'FREELANCER' || userRole === 'AGENCY') {
        console.log('Navigating to /new-service');
        navigate('/new-service');
      } else {
        console.log('Navigating to /register - no role or not logged in');
        console.log('userRole type:', typeof userRole, 'value:', userRole);
        navigate('/register');
      }
    }
  };

  const handleFindTalent = () => {
    setMobileOpen(false);
    if (onFindTalent) {
      onFindTalent();
    } else {
      navigate('/find-talent');
    }
  };

  const handleFindWork = () => {
    setMobileOpen(false);
    navigate('/find-work');
  };

  const handleFindServices = () => {
    setMobileOpen(false);
    navigate('/find-services');
  };

  const handleNotifications = () => {
    setMobileOpen(false);
    navigate('/notifications');
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const drawer = (
    <Box sx={{ width: 280, height: '100%', backgroundColor: '#0f0f23', overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32 }}>
            W
          </Avatar>
          <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: '#28A0F0' }}>
            {t('header.logo')}
          </Typography>
        </Box>
        <IconButton
          color="inherit"
          onClick={handleDrawerToggle}
          sx={{ color: '#ffffff' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      
      <List sx={{ px: 2, py: 2 }}>
        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton
            onClick={() => {
              setMobileOpen(false);
              navigate('/');
            }}
            sx={{
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'rgba(40, 160, 240, 0.1)',
              },
            }}
          >
            <ListItemText 
              primary={t('header.home')} 
              sx={{ 
                '& .MuiListItemText-primary': { 
                  color: '#ffffff',
                  fontWeight: 500,
                } 
              }} 
            />
          </ListItemButton>
        </ListItem>
        
        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton
            onClick={handleFindTalent}
            sx={{
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'rgba(40, 160, 240, 0.1)',
              },
            }}
          >
            <ListItemText 
              primary={t('header.findTalent')} 
              sx={{ 
                '& .MuiListItemText-primary': { 
                  color: '#ffffff',
                  fontWeight: 500,
                } 
              }} 
            />
          </ListItemButton>
        </ListItem>
        
        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton
            onClick={handleFindWork}
            sx={{
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'rgba(40, 160, 240, 0.1)',
              },
            }}
          >
            <ListItemText 
              primary={t('header.findWork')} 
              sx={{ 
                '& .MuiListItemText-primary': { 
                  color: '#ffffff',
                  fontWeight: 500,
                } 
              }} 
            />
          </ListItemButton>
        </ListItem>
        
        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton
            onClick={handleFindServices}
            sx={{
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'rgba(40, 160, 240, 0.1)',
              },
            }}
          >
            <ListItemText 
              primary={t('header.services')} 
              sx={{ 
                '& .MuiListItemText-primary': { 
                  color: '#ffffff',
                  fontWeight: 500,
                } 
              }} 
            />
          </ListItemButton>
        </ListItem>
        
        {/* Notifications - Only for logged in users */}
        {isLoggedIn && (
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={handleNotifications}
              sx={{
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: 'rgba(40, 160, 240, 0.1)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <NotificationsIcon sx={{ mr: 2, color: '#ffffff' }} />
                <ListItemText 
                  primary={t('notifications.title')} 
                  sx={{ 
                    '& .MuiListItemText-primary': { 
                      color: '#ffffff',
                      fontWeight: 500,
                    } 
                  }} 
                />
                {unreadCount > 0 && (
                  <Badge
                    badgeContent={unreadCount}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#ff6b6b',
                        color: '#ffffff',
                        fontWeight: 'bold',
                      },
                    }}
                  />
                )}
              </Box>
            </ListItemButton>
          </ListItem>
        )}
        
      </List>
      
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 2 }} />
      
      
      <Box sx={{ px: 2, py: 2 }}>
        <Box sx={{ mb: 3 }}>
          <LanguageSwitcher isMobileMenu={true} />
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="contained"
            onClick={isLoggedIn ? handleAccountMenuOpen : handleAuthMenuOpen}
            endIcon={<ExpandMoreIcon />}
            sx={{
              background: isLoggedIn 
                ? 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)'
                : 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
              color: '#ffffff',
              px: 2,
              py: 1.5,
              fontWeight: 600,
              '&:hover': {
                background: isLoggedIn
                  ? 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)'
                  : 'linear-gradient(135deg, #be185d 0%, #9d174d 100%)',
              },
            }}
          >
            {isLoggedIn ? t('header.account') : `${t('header.logIn')}/${t('header.signUp')}`}
          </Button>
          
          
          {isLoggedIn && (
            <Button
              variant="contained"
              onClick={handlePostProject}
              sx={{
                background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                color: '#ffffff',
                px: 2,
                py: 1.5,
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #be185d 0%, #9d174d 100%)',
                }
              }}
            >
              {userRole === 'CLIENT' ? t('header.postProject') : 
               userRole === 'FREELANCER' || userRole === 'AGENCY' ? t('header.postService') : 
               t('header.postProject')}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        color="default" 
        elevation={0}
        sx={{
          backgroundColor: 'rgba(15, 15, 35, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          {/* Logo */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexGrow: 1,
              cursor: 'pointer',
            }}
            onClick={handleLogoClick}
          >
            <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32 }}>
              W
            </Avatar>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: '#28A0F0' }}>
              {t('header.logo')}
            </Typography>
          </Box>
          
          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5 }}>
            <Button 
              color="inherit" 
              sx={{ 
                color: '#ffffff',
                fontSize: '0.9rem',
                px: 1.5,
                py: 0.5,
                minWidth: 'auto',
              }}
              onClick={() => navigate('/')}
            >
              {t('header.home')}
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: '#ffffff',
                fontSize: '0.9rem',
                px: 1.5,
                py: 0.5,
                minWidth: 'auto',
              }}
              onClick={handleFindServices}
            >
              {t('header.services')}
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: '#ffffff',
                fontSize: '0.9rem',
                px: 1.5,
                py: 0.5,
                minWidth: 'auto',
              }}
              onClick={handleFindTalent}
            >
              {t('header.findTalent')}
            </Button>
            <Button 
              color="inherit" 
              sx={{ 
                color: '#ffffff',
                fontSize: '0.9rem',
                px: 1.5,
                py: 0.5,
                minWidth: 'auto',
              }}
              onClick={handleFindWork}
            >
              {t('header.findWork')}
            </Button>
          </Box>

          {/* Desktop Actions */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5, ml: 2 }}>
            <LanguageSwitcher />
            
            {/* Notifications Button - Only for logged in users */}
            {isLoggedIn && (
              <IconButton
                onClick={handleNotifications}
                sx={{
                  color: '#ffffff',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: 'rgba(40, 160, 240, 0.1)',
                  },
                }}
              >
                {unreadCount > 0 ? (
                  <Badge
                    badgeContent={unreadCount}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#ff6b6b',
                        color: '#ffffff',
                        fontWeight: 'bold',
                      },
                    }}
                  >
                    <NotificationsIcon />
                  </Badge>
                ) : (
                  <NotificationsIcon />
                )}
              </IconButton>
            )}
            
            {/* Account Button */}
            <Button
              variant="contained"
              onClick={isLoggedIn ? handleAccountMenuOpen : handleAuthMenuOpen}
              endIcon={<ExpandMoreIcon />}
              sx={{
                background: isLoggedIn 
                  ? 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)'
                  : 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                color: '#ffffff',
                px: 2,
                py: 1,
                fontSize: '0.9rem',
                fontWeight: 600,
                minWidth: 'auto',
                '&:hover': {
                  background: isLoggedIn
                    ? 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)'
                    : 'linear-gradient(135deg, #be185d 0%, #9d174d 100%)',
                },
              }}
            >
              {isLoggedIn ? t('header.account') : `${t('header.logIn')}/${t('header.signUp')}`}
            </Button>
            
            {isLoggedIn && (
              <Button 
                variant="contained" 
                sx={{ 
                  background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                  color: '#ffffff',
                  px: 2,
                  py: 1,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  minWidth: 'auto',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #be185d 0%, #9d174d 100%)',
                  }
                }}
                onClick={handlePostProject}
              >
                {userRole === 'CLIENT' ? 'Публикувай проект' : 
                 userRole === 'FREELANCER' || userRole === 'AGENCY' ? 'Публикувай услуга' : 
                 'Публикувай проект'}
              </Button>
            )}
          </Box>

          {/* Mobile Menu Button */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              display: { md: 'none' },
              color: '#ffffff',
              ml: 2,
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            backgroundColor: '#0f0f23',
            borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Account Dropdown Menu */}
      <Menu
        anchorEl={accountMenuAnchor}
        open={Boolean(accountMenuAnchor)}
        onClose={handleAccountMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a2e',
            border: '1px solid #2d2d44',
            mt: 1,
            minWidth: 200,
            '& .MuiMenuItem-root': {
              color: '#ffffff',
              px: 3,
              py: 1.5,
              '&:hover': {
                backgroundColor: 'rgba(40, 160, 240, 0.1)',
              },
            },
          },
        }}
      >
        {/* Credits Tab */}
        {isLoggedIn && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              background: credits?.credits_balance === 0 
                ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.9) 0%, rgba(220, 38, 38, 0.9) 100%)'
                : 'linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(5, 150, 105, 0.9) 100%)',
              color: '#ffffff',
              px: 3,
              py: 2,
              mx: 1,
              mb: 1,
              fontWeight: 600,
              borderRadius: 1,
              border: credits?.credits_balance === 0 
                ? '2px solid rgba(239, 68, 68, 0.5)'
                : '2px solid rgba(16, 185, 129, 0.5)',
              boxShadow: credits?.credits_balance === 0 
                ? '0 0 10px rgba(239, 68, 68, 0.3)'
                : '0 0 10px rgba(16, 185, 129, 0.3)',
              animation: credits?.credits_balance === 0 ? 'glow 2s ease-in-out infinite alternate' : 'none',
              '@keyframes glow': {
                '0%': {
                  boxShadow: '0 0 5px rgba(239, 68, 68, 0.3), 0 0 10px rgba(239, 68, 68, 0.2)',
                },
                '100%': {
                  boxShadow: '0 0 15px rgba(239, 68, 68, 0.5), 0 0 25px rgba(239, 68, 68, 0.3)',
                },
              },
            }}
          >
            <CreditCardIcon sx={{ mr: 2, fontSize: 20 }} />
            {credits?.credits_balance || 0} {t('credits.credits')}
          </Box>
        )}
        
        <MenuItem onClick={handleViewProfile}>
          <PersonIcon sx={{ mr: 2, fontSize: 20 }} />
          {t('profile.title')}
        </MenuItem>
        <MenuItem onClick={handleBuyCredits}>
          <AddIcon sx={{ mr: 2, fontSize: 20 }} />
          {t('credits.purchase.buyButton')}
        </MenuItem>
        <MenuItem onClick={handleCreditHistory}>
          <CreditCardIcon sx={{ mr: 2, fontSize: 20 }} />
          {t('credits.history.title')}
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 2, fontSize: 20 }} />
          {t('common.logout')}
        </MenuItem>
      </Menu>

      {/* Auth Dropdown Menu */}
      <Menu
        anchorEl={authMenuAnchor}
        open={Boolean(authMenuAnchor)}
        onClose={handleAuthMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a2e',
            border: '1px solid #2d2d44',
            mt: 1,
            minWidth: 200,
            '& .MuiMenuItem-root': {
              color: '#ffffff',
              px: 3,
              py: 1.5,
              '&:hover': {
                backgroundColor: 'rgba(40, 160, 240, 0.1)',
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleLogin}>
          <PersonIcon sx={{ mr: 2, fontSize: 20 }} />
          {t('header.logIn')}
        </MenuItem>
        <MenuItem onClick={handleRegister}>
          <AccountIcon sx={{ mr: 2, fontSize: 20 }} />
          {t('header.signUp')}
        </MenuItem>
      </Menu>
    </>
  );
};

export default Header;
